/**
 * 運営の制限 (凍結 / 一時制限 / 低速モード) を「止められた瞬間」に正しく伝える。
 *
 * 2026-09-21 まで、投稿アクションの fetch は 401 と 403 をひとまとめにして
 * 「ログインが必要です」と出していた。実際には 403 の中身は
 *   ・アカウントが凍結されている
 *   ・アカウントが一時制限中
 *   ・CSRF が切れた
 * の 3 つで、**ログインはしている**。オーナー報告「ログインをしているにも
 * かかわらず、ログインを要求されて、いいねが押せない」はこれ。
 * 429 (低速モード・連打) に至ってはサーバが用意した本文を捨てて
 * 「操作に失敗しました」に潰していた。
 *
 * ここが「サーバの応答 → 利用者に見せる 1 行」の唯一の変換器。
 * ⚠ 文言はサーバ (internal/moderation/limit.go) が正本。ここで作文しない —
 *    作文すると、帯・アカウントの状態のページと言うことが食い違う。
 *    下の fallback は **サーバが本文を返さなかったときだけ** の受け皿。
 */

import { u } from "./url";

export interface Block {
  /** 利用者に見せる 1 行。 */
  message: string;
  /** "suspended" / "restricted" / "slow" / "" (制限ではない失敗)。 */
  kind: string;
  /** 本当にログインが必要か (401 だけ)。 */
  needsLogin: boolean;
  /** アカウントの状態のページへ案内してよいか。 */
  detail: boolean;
}

interface Envelope {
  error?: string;
  /** modgate だけが入れる。制限の種類。 */
  limit?: string;
  suspended?: boolean;
  restricted?: boolean;
}

const LIMIT_KINDS = ["suspended", "restricted", "slow"];

/**
 * 失敗レスポンスを読み解く。制限でも何でもない失敗 (500 等) は null。
 *
 * ⚠ res.clone() で読む — 呼び出し側が本文をもう一度読みたいことがある。
 */
export async function readBlock(res: Response): Promise<Block | null> {
  if (res.ok) return null;

  // 401 だけが本物の「ログインが必要」。
  if (res.status === 401) {
    return { message: "ログインが必要です", kind: "", needsLogin: true, detail: false };
  }
  if (res.status !== 403 && res.status !== 429) return null;

  let env: Envelope = {};
  try {
    env = (await res.clone().json()) as Envelope;
  } catch {
    // JSON ではない (CSRF 失敗の素のテキストなど)。
  }

  let kind = typeof env.limit === "string" ? env.limit : "";
  if (!kind && env.suspended === true) kind = "suspended";
  if (kind && LIMIT_KINDS.indexOf(kind) < 0) kind = "";

  const message = (env.error || "").trim() || fallback(res.status);
  return { message, kind, needsLogin: false, detail: kind !== "" };
}

/** サーバが本文を返さなかったときだけ使う受け皿。 */
function fallback(status: number): string {
  if (status === 429) return "操作が続きすぎました。少し時間を空けてください";
  // 403 で中身が無いのは、ほぼ CSRF 切れ (ページを開きっぱなしにしていた)。
  return "セッションが切れています。ページを更新してください";
}

/**
 * 制限のトーストを出す。制限 (凍結 / 一時制限 / 低速モード) のときだけ
 * 「詳細」をピルの末尾に足して、アカウントの状態のページへ案内する。
 *
 * ⚠ 連打の即時ブロック (kind なし) には「詳細」を付けない — そこへ行っても
 *    見るものが無い (状態としては何も残っていない)。
 */
export function showBlockToast(b: Block): void {
  const show = window.showToast;
  if (typeof show !== "function") {
    window.alert(b.message);
    return;
  }
  show(b.message, b.needsLogin ? "warn" : b.kind === "suspended" ? "error" : "warn");
  if (b.detail) attachDetailLink();
}

/**
 * いま出したピルの末尾に「詳細」を足す (402 の「投稿して稼ぐ」と同じ作法)。
 * toast.ts は文字しか持たないので、DOM に後から足す。
 */
function attachDetailLink(): void {
  const stack = document.getElementById("mpToastStack");
  const toast = stack ? stack.lastElementChild : null;
  const inner = toast ? toast.querySelector<HTMLElement>(".mp-toast-inner") : null;
  if (!inner || inner.querySelector(".mp-toast-earn")) return;
  const a = document.createElement("a");
  // 見た目は 402 の「投稿して稼ぐ」と同じピル内リンクを流用する
  // (専用クラスを増やすと theme-light の白文字登録も 2 つになる)。
  a.className = "mp-toast-earn";
  a.href = u("/appeal");
  a.textContent = "詳細";
  inner.appendChild(a);
}

/**
 * fetch の失敗をそのまま受けて、制限なら出して true を返す。
 * 制限ではない失敗は false (呼び出し側が従来どおり扱う)。
 */
export async function handleBlock(res: Response): Promise<boolean> {
  const b = await readBlock(res);
  if (!b) return false;
  showBlockToast(b);
  return true;
}

declare global {
  interface Window {
    showToast?: (msg: string, type?: "info" | "success" | "warn" | "error") => void;
  }
}
